import { useContext, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GlobalContext } from "../GlobalContext";
import { useToast } from "../ToastContext";
import { apiFetch } from "../api.js";
import fields from "../fields";
import { normalizeRecords, normalizeValue } from "../UtilityFunctions";

const EMPTY = [];

/**
 * Sheet data on react-query instead of SubNav's hand-rolled useState + fetch +
 * socket-patching. One call per sheet:
 *
 *   const { data, setData } = useSheetData("Brokers", "/getbrokers", {
 *       pagination, setPagination
 *   });
 *
 * - The query key carries only REQUEST params (page, page size, search, dates);
 *   totals coming back from the server are merged into the pagination state via
 *   setPagination, and page changes refetch automatically through the key.
 * - Socket add/replace/update/delete events patch the cached records with the
 *   same normalization as fetches; 'connect' and server 'refetch' events
 *   invalidate the sheet's queries.
 * - setData exists for useTabularDataManager's sortData, which expects a
 *   useState-style setter. It writes through to the cache (copying, since
 *   sortArray sorts in place).
 * - transformRecord (optional, must be a stable function) post-processes each
 *   record — e.g. Customers' CustomerName concatenation.
 * - enabled (optional, default true) gates the FETCH only, not the hook. Pass
 *   `activeTab === "tab-inventory"` and the sheet is downloaded when the user
 *   first opens it rather than at login. The socket listeners below stay
 *   subscribed either way, so a cached sheet the user has already visited keeps
 *   receiving live patches while it is off-screen and does not go stale.
 */
export default function useSheetData(sheetName, path, { pagination = null, setPagination = null, transformRecord = null, enabled = true } = {}) {
    const { DealershipID, userData, socket } = useContext(GlobalContext);
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    const requestParams = pagination ? {
        currentPage: pagination.currentPage,
        recordsPerPage: pagination.recordsPerPage,
        searchTerm: pagination.searchTerm ?? "",
        dateFrom: pagination.dateFrom ?? "",
        dateTo: pagination.dateTo ?? "",
    } : null;

    const queryKey = ["sheet", sheetName, DealershipID, requestParams];

    const query = useQuery({
        queryKey,
        enabled: !!DealershipID && enabled,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            let res;
            try {
                res = await apiFetch(path, {
                    body: { DealershipID, UserID: userData?.UserID, pagination }
                });
            } catch (err) { //Fetch only rejects on network failure
                err.isNetworkError = true;
                throw err;
            }
            if (res.status === 401) throw new Error("Unauthorised access. Please log in.");
            const payload = await res.json();
            if (res.status !== 200) throw new Error(payload.message || `HTTP ${res.status}`);
            let records = normalizeRecords(payload.data, fields);
            if (transformRecord) records = records.map(transformRecord);
            return { records, pagination: payload.pagination ?? null };
        },
    });

    //Merge server-side pagination totals back into the page-control state
    const serverPagination = query.data?.pagination;
    useEffect(() => {
        if (!serverPagination || !setPagination) return;
        if (typeof serverPagination === "object" &&
            "currentPage" in serverPagination && "totalPages" in serverPagination &&
            "totalRecords" in serverPagination && "recordsPerPage" in serverPagination) {
            setPagination(prev => ({ ...prev, ...serverPagination }));
        }
    }, [serverPagination, setPagination]);

    //Read-path failures are background events (react-query has already
    //retried) — surface them as a toast, not a blocking modal. Failed WRITES
    //elsewhere still use the modal, where blocking is appropriate.
    const queryError = query.error;
    useEffect(() => {
        if (!queryError) return;
        showToast('error', queryError.isNetworkError
            ? "Can't reach the server — check your connection."
            : `Couldn't load ${sheetName}: ${queryError.message}`,
        6000);
    }, [queryError, sheetName, showToast]);

    //The socket handlers below are registered once but must patch the
    //currently-active page's cache entry
    const queryKeyRef = useRef(queryKey);
    useEffect(() => { queryKeyRef.current = queryKey; });

    useEffect(() => {
        if (!socket) return;

        const patchRecords = (fn) => {
            queryClient.setQueryData(queryKeyRef.current, (old) => old ? { ...old, records: fn(old.records) } : old);
        };

        const addRecordHandler = (msg) => {
            if (msg.sheetName !== sheetName) return;
            let record = normalizeRecords([msg.newRecord], fields)[0];
            if (transformRecord) record = transformRecord(record);
            patchRecords((records) => [...records, record]);
        };

        const replaceRecordHandler = (msg) => {
            if (msg.sheetName !== sheetName) return;
            let record = normalizeRecords([msg.parentRecord], fields)[0];
            if (transformRecord) record = transformRecord(record);
            patchRecords((records) => records.map((row) =>
                row[msg.primaryKeyName] === msg.primaryKeyValue ? record : row));
        };

        const updateRecordHandler = (msg) => {
            if (msg.sheetName !== sheetName) return;
            //Failure echo to the sender — don't store the rejected value, but do say why.
            //The grid reverts the cell by itself, so staying silent left the user watching
            //their edit undo itself with no explanation. The modals have always shown
            //msg.message (AddEditInventory.jsx) and the server populates it
            //(sockets/updateField.js deny()). The echo is socket.emit to the sender only,
            //and the sheetName guard above means exactly one sheet instance toasts.
            if (msg.ok === false) {
                showToast('error', msg.message || 'That change could not be saved. It has been reverted.');
                return;
            }
            const value = normalizeValue(msg.fieldName, msg.fieldValue, fields);
            patchRecords((records) => records.map((row) =>
                row[msg.primaryKeyName] === msg.primaryKeyValue ? { ...row, [msg.fieldName]: value } : row));
        };

        const deleteRecordHandler = (msg) => {
            if (msg.sheetName !== sheetName) return;
            patchRecords((records) => records.filter((row) => row[msg.primaryKeyName] !== msg.primaryKeyValue));
        };

        const connectHandler = () => {
            queryClient.invalidateQueries({ queryKey: ["sheet", sheetName] });
        };

        const refetchHandler = (msg) => {
            if (msg?.sheetName !== sheetName) return;
            queryClient.invalidateQueries({ queryKey: ["sheet", sheetName] });
        };

        socket.on("add_record", addRecordHandler);
        socket.on("replace_record", replaceRecordHandler);
        socket.on("update_record", updateRecordHandler);
        socket.on("delete_record", deleteRecordHandler);
        socket.on("connect", connectHandler);
        socket.on("refetch", refetchHandler);

        return () => {
            socket.off("add_record", addRecordHandler);
            socket.off("replace_record", replaceRecordHandler);
            socket.off("update_record", updateRecordHandler);
            socket.off("delete_record", deleteRecordHandler);
            socket.off("connect", connectHandler);
            socket.off("refetch", refetchHandler);
        };
        //showToast is useCallback([generateId]) over a []-stable generateId, so listing
        //it here does not re-subscribe the sockets on every render.
    }, [socket, sheetName, queryClient, transformRecord, showToast]);

    //useState-style setter for legacy callers (useTabularDataManager.sortData)
    const setData = useCallback((updater) => {
        queryClient.setQueryData(queryKeyRef.current, (old) => {
            if (!old) return old;
            const next = typeof updater === "function" ? updater(old.records) : updater;
            return { ...old, records: [...next] };
        });
    }, [queryClient]);

    return {
        data: query.data?.records ?? EMPTY,
        isLoading: query.isPending,
        refetch: query.refetch,
        setData,
    };
}
